import { z } from 'zod';
/**
 * La spec de bloques de Athena (`plan/PLAN.md` § 4.7), que es el contrato entre el modelo, el motor
 * y la web.
 *
 * Tres ideas, y las tres son decisiones del plan y no gusto:
 *
 * 1. **El modelo no maqueta.** Elige qué tipo de objeto de evidencia pide la respuesta y escribe su
 *    texto; cómo se ve eso en escritorio y en móvil lo decide el renderer. Por eso no hay `Fila`,
 *    ni `Columna`, ni `Tabla`.
 * 2. **Lista plana y ordenada, sin hijos y sin árbol.** Un bloque no contiene otros bloques. La
 *    única excepción es `Tarjeta`, que no es un bloque suelto: solo existe dentro de `LadoALado`.
 * 3. **Catálogo cerrado.** Nueve tipos, ni uno más. Tres los elige el modelo en F2, dos más en F3, uno
 *    (`Grafico`, GRA-01) y tres los pone el motor y el modelo no los toca.
 *
 * Una referencia (`a:<idHecho>`) no la escribe el modelo como texto libre: nombra un hecho del
 * corpus y el motor la pinta con su cita, su página y su fuente. Así una referencia no puede
 * apuntar a algo que no existe.
 */
export const VERSION_SPEC = 'athena-1';
/**
 * El id de un hecho: un uuid (corpus clásico, y las respuestas guardadas con él) o el hash de 32
 * hexadecimales que pone la ingesta por lotes (`lotes.hechos.id`).
 */
export const ID_HECHO = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32}/u;
/**
 * El número de un hecho en la lista que recibe el compositor (BUS-37, idea de Iván): cita `a:7` en
 * vez de copiar un id de 32 caracteres, que copiaba mal. El motor lo cambia por el id **antes** de
 * validar, así que a la web y a la base solo llegan ids.
 */
export const POSICION_DE_HECHO = /\d{1,3}/u;
/**
 * Una referencia a un hecho del corpus. El modelo solo puede escribir `a:` seguido del id de un
 * hecho que se le ha dado (o de su número en la lista), y el validador comprueba que ese hecho
 * estaba entre los que recibió.
 */
export const referenciaSchema = z
    .string()
    .regex(new RegExp(`^a:(?:${ID_HECHO.source}|${POSICION_DE_HECHO.source})$`, 'u'), 'una referencia es "a:" y el id o el número de un hecho');
/** El id del hecho que hay dentro de una referencia. */
export function idDeLaReferencia(referencia) {
    return referencia.slice('a:'.length);
}
/**
 * El tope de un párrafo. Era 1.200 y la salida estructurada **corta al modelo en seco** al llegar:
 * en las respuestas amplias seguía en un bloque nuevo a mitad de palabra («más id» / «adecuado»), con
 * la cifra en un bloque y su marca en el otro, y `recolocarMarcas` no tocaba los párrafos al límite
 * (BUS-52). Pasaba en ~1 % de los párrafos de producción.
 */
export const LARGO_MAXIMO_DEL_PARRAFO = 2400;
/** Prosa con cifras y su marca [n]. Lo escribe el modelo y de estas frases responde el juez. */
export const parrafoSchema = z.object({
    tipo: z.literal('Parrafo'),
    texto: z.string().min(1).max(LARGO_MAXIMO_DEL_PARRAFO),
});
/**
 * Lo recuperado no permite responder esto, con lo más cercano que sí hay.
 *
 * Es el bloque que hace que Athena pueda decir que no, que es la mitad de lo que la hace útil.
 */
export const noMedidoSchema = z.object({
    tipo: z.literal('NoMedido'),
    texto: z.string().min(1).max(1200),
    alternativa: z.array(referenciaSchema).max(4),
});
/** La frase que el usuario se lleva, con las citas que la sostienen. */
export const fraseCopiableSchema = z.object({
    tipo: z.literal('FraseCopiable'),
    texto: z.string().min(1).max(600),
    citas: z.array(referenciaSchema).min(1).max(6),
});
/**
 * Las seis categorías de `Contrastar` y las tres de `Comparar` (F3). Viven aquí porque el
 * validador tiene que poder comprobarlas, aunque el bloque no se use hasta F3.
 */
export const CATEGORIAS_VEREDICTO = [
    'cifra_real_mismo_significado',
    'cifra_real_mide_otra_cosa',
    'cifra_real_otra_poblacion',
    'cifra_real_otro_ano',
    'no_medido_en_el_catalogo',
    'cifra_que_no_aparece',
    'se_pueden_poner_al_lado',
    'se_pueden_poner_al_lado_no_restar',
    'no_se_pueden_comparar',
];
export const veredictoSchema = z.object({
    tipo: z.literal('Veredicto'),
    categoria: z.enum(CATEGORIAS_VEREDICTO),
    texto: z.string().min(1).max(1200),
});
/**
 * Una de las dos caras de `LadoALado`. **El motor rellena quién, qué preguntó, cuándo y quién paga
 * desde la ficha de la fuente**: el modelo solo aporta la etiqueta y la frase. Así la tarjeta
 * siempre enseña los datos reales de transparencia.
 */
export const tarjetaSchema = z.object({
    etiqueta: z.string().min(1).max(80),
    afirmacion: referenciaSchema,
    frase: z.string().min(1).max(400),
});
/**
 * Dos tarjetas, exactamente dos, y **no una tupla de zod**.
 *
 * Una tupla se describe en JSON Schema con `items: [A, B]`, y el proveedor de modelos lo rechaza:
 * `Array types must be specified with a single object schema for 'items'`. Medido contra Zen el
 * 2026-09-22 con el esquema del compositor entero. Un array con largo fijo dice lo mismo, lo
 * impone igual y sí se puede pedir.
 */
export const ladoALadoSchema = z.object({
    tipo: z.literal('LadoALado'),
    tarjetas: z.array(tarjetaSchema).length(2),
});
/** Ocho representaciones; orientación y número de series no son tipos adicionales. */
export const FORMAS_DE_GRAFICO = ['barras', 'agrupadas', 'apiladas', 'linea', 'area', 'dona', 'dispersion', 'combinado'];
/**
 * Un punto del gráfico: una etiqueta corta y **el hecho del que sale la cifra**.
 *
 * El modelo no escribe ningún valor. La cifra, su unidad y su página las pone el motor desde el
 * hecho (`grafico.ts`), igual que la tarjeta de `LadoALado` toma quién y cuándo de la ficha. Así un
 * punto no puede decir algo distinto de lo que dice su fuente.
 */
export const puntoDeGraficoSchema = z.object({
    etiqueta: z.string().min(1).max(120),
    hecho: referenciaSchema,
    serie: z.string().min(1).max(80).nullable().optional(),
    hechoX: referenciaSchema.nullable().optional(),
});
/** Gráfico de observaciones citadas, al menos dos. El pie explica el alcance de la
 * interpretación. Opcional en lectura para conservar respuestas anteriores a GRA-18.
 * El compositor nuevo debe producirlo; el juez revisa el significado del conjunto.
 */
export const graficoSchema = z.object({
    tipo: z.literal('Grafico'),
    forma: z.enum(FORMAS_DE_GRAFICO),
    titulo: z.string().min(1).max(140),
    pie: z.string().max(700).optional(),
    orientacion: z.enum(['horizontal', 'vertical']).nullable().optional(),
    puntos: z.array(puntoDeGraficoSchema).min(2).max(60),
});
/** Los tres tipos de nota que pone el motor: lo que ha entendido, las reservas y el juez caído. */
export const TIPOS_DE_NOTA = ['supuesto', 'reserva', 'juez_no_disponible'];
export const notaSchema = z.object({
    tipo: z.literal('Nota'),
    clase: z.enum(TIPOS_DE_NOTA),
    texto: z.string().min(1).max(600),
});
/** La lista de fuentes con sus seis hechos de transparencia. La pone el motor, sin props. */
export const listaFuentesSchema = z.object({ tipo: z.literal('ListaFuentes') });
/** Tres formas de seguir preguntando. Las pone el motor con lo que el modelo sugirió. */
export const sugerenciasSchema = z.object({ tipo: z.literal('Sugerencias') });
/** Cuántos estudios lleva como mucho un `ListaEstudios`, y cuántas palabras de búsqueda. */
export const MAXIMO_ESTUDIOS_EN_LISTA = 25;
export const MAXIMO_PALABRAS_DEL_CATALOGO = 6;
/**
 * CATALOGO-EN-CONSULTA (Iván, 2026-10-17): la respuesta a «¿qué estudios tenéis?». La pone el motor
 * con lo que devuelve la búsqueda por texto en `fuentes`; el modelo no escribe nada sobre el
 * catálogo. `coincidencias` es cuántos estudios casan en total (puede pasar de los que se listan) y
 * `total` cuántos tiene el catálogo.
 */
export const listaEstudiosSchema = z.object({
    tipo: z.literal('ListaEstudios'),
    total: z.number().int().min(0),
    coincidencias: z.number().int().min(0),
    palabras: z.array(z.string().min(1).max(60)).max(MAXIMO_PALABRAS_DEL_CATALOGO),
    estudios: z
        .array(z.object({
        slug: z.string().min(1).max(200),
        titulo: z.string().min(1).max(400),
        editor: z.string().max(400),
        anio: z.number().int().nullable(),
    }))
        .max(MAXIMO_ESTUDIOS_EN_LISTA),
});
export const bloqueSchema = z.discriminatedUnion('tipo', [
    parrafoSchema,
    noMedidoSchema,
    fraseCopiableSchema,
    veredictoSchema,
    ladoALadoSchema,
    graficoSchema,
    notaSchema,
    listaFuentesSchema,
    sugerenciasSchema,
    listaEstudiosSchema,
]);
export const respuestaEnBloquesSchema = z.object({
    version: z.literal(VERSION_SPEC),
    bloques: z.array(bloqueSchema).min(1).max(40),
});
/** Los bloques que el modelo puede elegir. Los demás los pone el motor. */
export const BLOQUES_DEL_MODELO = ['Parrafo', 'NoMedido', 'FraseCopiable', 'Veredicto', 'LadoALado', 'Grafico'];
/** Los bloques que solo pone el motor. Si el modelo devuelve uno, el validador lo rechaza. */
export const BLOQUES_DEL_MOTOR = ['Nota', 'ListaFuentes', 'Sugerencias', 'ListaEstudios'];
//# sourceMappingURL=spec.js.map